import { useState, useEffect } from "react";
import { 
  Plus, Edit, Trash2, Eye, ToggleLeft, ToggleRight, 
  BadgePercent, Calendar, Users, TrendingUp, Copy, Check,
  DollarSign, Percent, AlertCircle, X
} from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PromoCodeManager = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUsageDialog, setShowUsageDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [usageData, setUsageData] = useState([]);
  const [copiedCode, setCopiedCode] = useState(null);
  const [filterActive, setFilterActive] = useState("all");
  
  const { getAuthHeader } = useAuth();

  const [formData, setFormData] = useState({
    code: "",
    description: "",
    discount_type: "percent",
    discount_value: "",
    min_purchase: "",
    max_discount: "",
    usage_limit: "",
    per_user_limit: "1",
    valid_from: "",
    valid_until: "",
    is_active: true
  });

  useEffect(() => {
    fetchPromoCodes();
  }, [filterActive]);

  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const params = filterActive === "active" ? "?active_only=true" : "";
      const response = await axios.get(`${API}/promo/codes${params}`, {
        headers: getAuthHeader()
      });
      setPromoCodes(response.data.promo_codes || []);
    } catch (error) {
      console.error("Error fetching promo codes:", error);
      toast.error("Failed to load promo codes");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      code: "",
      description: "",
      discount_type: "percent",
      discount_value: "",
      min_purchase: "",
      max_discount: "",
      usage_limit: "",
      per_user_limit: "1",
      valid_from: "",
      valid_until: "",
      is_active: true
    });
  };

  const handleCreateCode = async () => {
    if (!formData.code || !formData.discount_value) {
      toast.error("Code and discount value are required");
      return;
    }

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        description: formData.description || null,
        discount_type: formData.discount_type,
        discount_value: parseFloat(formData.discount_value),
        min_purchase: formData.min_purchase ? parseFloat(formData.min_purchase) : 0,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        per_user_limit: formData.per_user_limit ? parseInt(formData.per_user_limit) : 1,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
        is_active: formData.is_active
      };

      await axios.post(`${API}/promo/codes`, payload, {
        headers: getAuthHeader()
      });
      
      toast.success(`Promo code ${payload.code} created successfully!`);
      setShowCreateDialog(false);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to create promo code";
      toast.error(errorMsg);
    }
  };

  const handleEditCode = async () => {
    if (!selectedCode) return;

    try {
      const updates = {
        description: formData.description || null,
        discount_value: parseFloat(formData.discount_value),
        min_purchase: formData.min_purchase ? parseFloat(formData.min_purchase) : 0,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        usage_limit: formData.usage_limit ? parseInt(formData.usage_limit) : null,
        per_user_limit: formData.per_user_limit ? parseInt(formData.per_user_limit) : 1,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null,
        is_active: formData.is_active
      };

      await axios.patch(`${API}/promo/codes/${selectedCode.id}`, updates, {
        headers: getAuthHeader()
      });
      
      toast.success(`Promo code ${selectedCode.code} updated successfully!`);
      setShowEditDialog(false);
      setSelectedCode(null);
      resetForm();
      fetchPromoCodes();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to update promo code";
      toast.error(errorMsg);
    }
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Are you sure you want to delete promo code "${code.code}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/promo/codes/${code.id}`, {
        headers: getAuthHeader()
      });
      toast.success(`Promo code ${code.code} deleted`);
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to delete promo code");
    }
  };

  const handleToggleActive = async (code) => {
    try {
      await axios.patch(`${API}/promo/codes/${code.id}`, 
        { is_active: !code.is_active },
        { headers: getAuthHeader() }
      );
      toast.success(`Promo code ${code.code} ${code.is_active ? 'deactivated' : 'activated'}`);
      fetchPromoCodes();
    } catch (error) {
      toast.error("Failed to update promo code");
    }
  };

  const viewUsage = async (code) => {
    try {
      const response = await axios.get(`${API}/promo/usage/${code.code}`, {
        headers: getAuthHeader()
      });
      setUsageData(response.data.usage || []);
      setSelectedCode(code);
      setShowUsageDialog(true);
    } catch (error) {
      toast.error("Failed to load usage data");
    }
  };

  const openEditDialog = (code) => {
    setSelectedCode(code);
    setFormData({
      code: code.code,
      description: code.description || "",
      discount_type: code.discount_type,
      discount_value: code.discount_value?.toString() || "",
      min_purchase: code.min_purchase?.toString() || "",
      max_discount: code.max_discount?.toString() || "",
      usage_limit: code.usage_limit?.toString() || "",
      per_user_limit: code.per_user_limit?.toString() || "1",
      valid_from: code.valid_from?.split("T")[0] || "",
      valid_until: code.valid_until?.split("T")[0] || "",
      is_active: code.is_active
    });
    setShowEditDialog(true);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    toast.success("Code copied to clipboard!");
  };

  const getStatusBadge = (code) => {
    if (!code.is_active) {
      return <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>;
    }
    
    const now = new Date();
    if (code.valid_from && new Date(code.valid_from) > now) {
      return <Badge className="bg-amber-100 text-amber-700">Scheduled</Badge>;
    }
    if (code.valid_until && new Date(code.valid_until) < now) {
      return <Badge className="bg-red-100 text-red-700">Expired</Badge>;
    }
    if (code.usage_limit && code.usage_count >= code.usage_limit) {
      return <Badge className="bg-red-100 text-red-700">Limit Reached</Badge>;
    }
    
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  // Stats calculation
  const totalCodes = promoCodes.length;
  const activeCodes = promoCodes.filter(c => c.is_active).length;
  const totalUsage = promoCodes.reduce((sum, c) => sum + (c.usage_count || 0), 0);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-brand/10">
              <BadgePercent className="h-5 w-5 text-brand" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalCodes}</p>
              <p className="text-sm text-muted-foreground">Total Codes</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-green-100">
              <ToggleRight className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{activeCodes}</p>
              <p className="text-sm text-muted-foreground">Active Codes</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-purple-100">
              <TrendingUp className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalUsage}</p>
              <p className="text-sm text-muted-foreground">Total Uses</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100">
              <Users className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalCodes - activeCodes}</p>
              <p className="text-sm text-muted-foreground">Inactive Codes</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={filterActive} onValueChange={setFilterActive}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Codes</SelectItem>
              <SelectItem value="active">Active Only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowCreateDialog(true); }}
          className="bg-brand hover:bg-brand-dark"
          data-testid="create-promo-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Promo Code
        </Button>
      </div>

      {/* Promo Codes List */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Promo Codes</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto"></div>
              <p className="text-muted-foreground mt-2">Loading promo codes...</p>
            </div>
          ) : promoCodes.length === 0 ? (
            <div className="text-center py-12">
              <BadgePercent className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Promo Codes Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first promo code to offer discounts to customers.
              </p>
              <Button 
                onClick={() => { resetForm(); setShowCreateDialog(true); }}
                className="bg-brand hover:bg-brand-dark"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Code
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Code</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Discount</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Usage</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Validity</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map((code) => (
                    <tr key={code.id} className="border-b hover:bg-slate-50" data-testid={`promo-row-${code.code}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-brand">{code.code}</span>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-6 w-6"
                            onClick={() => copyCode(code.code)}
                          >
                            {copiedCode === code.code ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                        {code.description && (
                          <p className="text-xs text-muted-foreground mt-1">{code.description}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {code.discount_type === "percent" ? (
                            <>
                              <Percent className="h-4 w-4 text-green-600" />
                              <span className="font-semibold">{code.discount_value}%</span>
                            </>
                          ) : (
                            <>
                              <DollarSign className="h-4 w-4 text-green-600" />
                              <span className="font-semibold">${code.discount_value}</span>
                            </>
                          )}
                        </div>
                        {code.min_purchase > 0 && (
                          <p className="text-xs text-muted-foreground">Min: ${code.min_purchase}</p>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-semibold">{code.usage_count || 0}</span>
                        {code.usage_limit && (
                          <span className="text-muted-foreground">/{code.usage_limit}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {code.valid_from || code.valid_until ? (
                          <div className="text-xs">
                            {code.valid_from && <p>From: {new Date(code.valid_from).toLocaleDateString()}</p>}
                            {code.valid_until && <p>Until: {new Date(code.valid_until).toLocaleDateString()}</p>}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">No limits</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {getStatusBadge(code)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => viewUsage(code)}
                            title="View Usage"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => openEditDialog(code)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleToggleActive(code)}
                            title={code.is_active ? "Deactivate" : "Activate"}
                          >
                            {code.is_active ? (
                              <ToggleRight className="h-4 w-4 text-green-600" />
                            ) : (
                              <ToggleLeft className="h-4 w-4 text-slate-400" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600 hover:text-red-700"
                            onClick={() => handleDeleteCode(code)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create Promo Code</DialogTitle>
            <DialogDescription>
              Create a new discount code for customers to use at checkout.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="code">Code *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="SUMMER20"
                  className="uppercase"
                  data-testid="input-code"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="discount_type">Discount Type</Label>
                <Select 
                  value={formData.discount_type} 
                  onValueChange={(v) => setFormData({ ...formData, discount_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discount_value">
                  Discount Value * {formData.discount_type === "percent" ? "(%)" : "($)"}
                </Label>
                <Input
                  id="discount_value"
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                  placeholder={formData.discount_type === "percent" ? "20" : "50"}
                  data-testid="input-discount-value"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_discount">Max Discount ($)</Label>
                <Input
                  id="max_discount"
                  type="number"
                  value={formData.max_discount}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                  placeholder="100"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="20% off for new customers"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_purchase">Min Purchase ($)</Label>
                <Input
                  id="min_purchase"
                  type="number"
                  value={formData.min_purchase}
                  onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="usage_limit">Usage Limit</Label>
                <Input
                  id="usage_limit"
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valid_from">Valid From</Label>
                <Input
                  id="valid_from"
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="valid_until">Valid Until</Label>
                <Input
                  id="valid_until"
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="is_active">Active Status</Label>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateCode} className="bg-brand hover:bg-brand-dark" data-testid="save-promo-btn">
              Create Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Promo Code</DialogTitle>
            <DialogDescription>
              Update the settings for {selectedCode?.code}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Code</Label>
                <Input value={formData.code} disabled className="bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Input value={formData.discount_type === "percent" ? "Percentage" : "Fixed"} disabled className="bg-slate-50" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_discount_value">
                  Discount Value {formData.discount_type === "percent" ? "(%)" : "($)"}
                </Label>
                <Input
                  id="edit_discount_value"
                  type="number"
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_max_discount">Max Discount ($)</Label>
                <Input
                  id="edit_max_discount"
                  type="number"
                  value={formData.max_discount}
                  onChange={(e) => setFormData({ ...formData, max_discount: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_description">Description</Label>
              <Textarea
                id="edit_description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_min_purchase">Min Purchase ($)</Label>
                <Input
                  id="edit_min_purchase"
                  type="number"
                  value={formData.min_purchase}
                  onChange={(e) => setFormData({ ...formData, min_purchase: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_usage_limit">Usage Limit</Label>
                <Input
                  id="edit_usage_limit"
                  type="number"
                  value={formData.usage_limit}
                  onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_valid_from">Valid From</Label>
                <Input
                  id="edit_valid_from"
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_valid_until">Valid Until</Label>
                <Input
                  id="edit_valid_until"
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="edit_is_active">Active Status</Label>
              <Switch
                id="edit_is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditCode} className="bg-brand hover:bg-brand-dark">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={showUsageDialog} onOpenChange={setShowUsageDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Usage History - {selectedCode?.code}</DialogTitle>
            <DialogDescription>
              View all uses of this promo code
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {usageData.length === 0 ? (
              <div className="text-center py-8">
                <TrendingUp className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">No usage recorded yet</p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Customer Email</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Discount</th>
                      <th className="text-left py-2 px-3 font-medium text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageData.map((usage, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-2 px-3 text-sm">{usage.customer_email}</td>
                        <td className="py-2 px-3 text-sm text-green-600 font-medium">
                          -${usage.discount_amount?.toFixed(2)}
                        </td>
                        <td className="py-2 px-3 text-sm text-muted-foreground">
                          {new Date(usage.used_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUsageDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromoCodeManager;
